import { mapValues, memoize } from '@exodus/basic-utils';
import ExchangeProviders, { AssetsService, GasPriceService, OrdersService, PricingService, Providers, } from '@exodus/exchange-client-providers';
import createFetchival from '@exodus/fetch/create-fetchival';
import lodash from 'lodash';
const MEMOIZATION_TIMEOUT = 1000 * 60 * 5;
const { debounce } = lodash;
export class ExchangeProvidersModule {
    #exchangeProviders;
    #geolocationAtom;
    #exchangeRemoteConfigAtom;
    #assetsModule;
    #enabledAssetsAtom;
    #assetsAtom;
    #exchangeableAssetsAtom;
    #canBuildAssets = true;
    #subscriptions = [];
    constructor({ config, fetch, geolocationAtom, logger, exchangeTracer, exchangeRemoteConfigAtom, assetsModule, enabledAssetsAtom, assetsAtom, exchangeableAssetsAtom, }) {
        const fetchival = createFetchival({ fetch });
        const ecpLogger = config.debug
            ? mapValues(logger, (loggerFn) => (...args) => loggerFn('exchange-client-providers', ...args))
            : undefined;
        const assetsService = new AssetsService({
            ...config.services.assets,
            fetchival,
        });
        const ordersService = new OrdersService({
            ...config.services.orders,
            fetchival,
        });
        const pricingService = new PricingService({
            ...config.services.pricing,
            fetchival,
        });
        const gasPriceService = new GasPriceService({
            ...config.services.gas,
            assetsService,
            fetchival,
            pricingService,
        });
        this.#exchangeProviders = new ExchangeProviders({
            providers: [
                config.providers.exodus.enabled &&
                    new Providers.Exodus({
                        ...config.providers.exodus,
                        fetchival,
                        assetsService,
                        tracer: exchangeTracer,
                    }),
                config.providers.jupiter?.enabled &&
                    new Providers.Jupiter({
                        ...config.providers.jupiter,
                        assetsService,
                        ordersService,
                        pricingService,
                        fetchival,
                        tracer: exchangeTracer,
                    }),
                config.providers.lifi?.enabled &&
                    new Providers.LiFi({
                        ...config.providers.lifi,
                        assetsService,
                        gasPriceService,
                        ordersService,
                        pricingService,
                        fetchival,
                        tracer: exchangeTracer,
                    }),
                config.providers.rango?.enabled &&
                    new Providers.Rango({
                        ...config.providers.rango,
                        assetsService,
                        gasPriceService,
                        ordersService,
                        pricingService,
                        fetchival,
                        tracer: exchangeTracer,
                    }),
            ].filter(Boolean),
            logger: ecpLogger,
            assetsService,
        });
        this.#geolocationAtom = geolocationAtom;
        this.#exchangeRemoteConfigAtom = exchangeRemoteConfigAtom;
        this.#enabledAssetsAtom = enabledAssetsAtom;
        this.#assetsModule = assetsModule;
        this.#assetsAtom = assetsAtom;
        this.#exchangeableAssetsAtom = exchangeableAssetsAtom;
        this.#canBuildAssets = config.prefetchAssetsOnLoad ?? true;
    }
    getProvider(...args) {
        return this.#exchangeProviders.getProvider(...args);
    }
    getSupportedProvidersForPair(...args) {
        return this.#exchangeProviders.getSupportedProvidersForPair(...args);
    }
    getSupportedFromAssetIds(...args) {
        return this.#withExchangeableAssets(() => this.#exchangeProviders.getSupportedFromAssetIds(...args));
    }
    getSupportedToAssetIds(...args) {
        return this.#withExchangeableAssets(() => this.#exchangeProviders.getSupportedToAssetIds(...args));
    }
    #withExchangeableAssets = async (fn) => {
        if (!this.#canBuildAssets) {
            await this.buildExchangeableAssets();
        }
        return fn();
    };
    getQuotes(...args) {
        return this.#exchangeProviders.getQuotes(...args);
    }
    prepareExchange(...args) {
        return this.#exchangeProviders.prepareExchange(...args);
    }
    executeExchange(...args) {
        return this.#exchangeProviders.executeExchange(...args);
    }
    getExchangeLimits(...args) {
        return this.#exchangeProviders.getExchangeLimits(...args);
    }
    isAssetExchangeableFrom(...args) {
        return this.#exchangeProviders.isAssetExchangeableFrom(...args);
    }
    isAssetExchangeable(...args) {
        return this.#exchangeProviders.isAssetExchangeable(...args);
    }
    isAssetExchangeableTo(...args) {
        return this.#exchangeProviders.isAssetExchangeableTo(...args);
    }
    load = () => {
        const geolocationSubscription = this.#geolocationAtom.observe(this.#handleGeolocationChange);
        const remoteConfigSubscription = this.#exchangeRemoteConfigAtom.observe(this.#handleRemoteConfigChange);
        const enabledAssetsAtomSubscription = this.#enabledAssetsAtom.observe(this.#handleEnabledAssetsChange);
        const assetsAtomSubscription = this.#assetsAtom.observe(this.#handleAvailableAssetsChange);
        this.#subscriptions.push(geolocationSubscription, remoteConfigSubscription, enabledAssetsAtomSubscription, assetsAtomSubscription);
    };
    buildExchangeableAssets = async () => {
        this.#canBuildAssets = true;
        const [assetsAtomValue, enabledAssetsAtomValue] = await Promise.all([
            this.#assetsAtom.get(),
            this.#enabledAssetsAtom.get(),
        ]);
        const assetIds = this.#sortAssetIds(Object.keys(assetsAtomValue.value));
        const enabledAssetIds = this.#sortAssetIds(Object.keys(enabledAssetsAtomValue).filter((assetName) => enabledAssetsAtomValue[assetName]));
        await Promise.all([
            this.#buildExchangeableToAssetIdsWithMemo(assetIds),
            this.#buildExchangeableFromAssetIdsWithMemo(enabledAssetIds),
        ]);
    };
    stop = () => {
        this.#subscriptions.forEach((unsubscribe) => unsubscribe());
    };
    #handleEnabledAssetsChange = debounce(async (value) => {
        if (!this.#canBuildAssets) {
            return;
        }
        const assetIds = this.#sortAssetIds(Object.keys(value).filter((assetName) => value[assetName]));
        void this.#buildExchangeableFromAssetIdsWithMemo(assetIds);
    }, 500);
    #buildExchangeableFromAssetIdsWithMemo = memoize(async (assetIds) => {
        const fromAssetIds = await this.#exchangeProviders.buildExchangeableFromAssetIds(assetIds);
        return this.#exchangeableAssetsAtom.set((s) => ({ ...s, fromAssetIds: [...fromAssetIds] }));
    }, (assetIds) => assetIds.join(','), MEMOIZATION_TIMEOUT);
    #handleAvailableAssetsChange = async ({ value }) => {
        if (!this.#canBuildAssets) {
            return;
        }
        const assetIds = this.#sortAssetIds(Object.keys(value));
        void this.#buildExchangeableToAssetIdsWithMemo(assetIds);
    };
    #sortAssetIds = (assetNames) => {
        return assetNames
            .map((assetName) => this.#assetsModule.getAsset(assetName).ticker)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    };
    #buildExchangeableToAssetIdsWithMemo = memoize(async (assetIds) => {
        const toAssetIds = await this.#exchangeProviders.buildExchangeableToAssetIds(assetIds);
        return this.#exchangeableAssetsAtom.set((s) => ({ ...s, toAssetIds: [...toAssetIds] }));
    }, (assetIds) => assetIds.join(','), MEMOIZATION_TIMEOUT);
    #handleGeolocationChange = (geolocationValue) => {
        this.#exchangeProviders.setGeolocation(geolocationValue);
    };
    #handleRemoteConfigChange = async (value) => {
        Object.entries(value).forEach(([providerName, providerConfig]) => {
            if (typeof providerConfig.enabled !== 'boolean') {
                return;
            }
            if (providerConfig.enabled) {
                this.#exchangeProviders.enableProvider(providerName);
            }
            else {
                this.#exchangeProviders.disableProvider(providerName);
            }
        });
    };
}
const exchangeProvidersModuleFactory = (args) => new ExchangeProvidersModule({ ...args });
const exchangeProvidersModuleDefinition = {
    id: 'exchangeProviders',
    type: 'module',
    factory: exchangeProvidersModuleFactory,
    dependencies: [
        'config',
        'logger',
        'geolocationAtom',
        'fetch',
        'exchangeTracer?',
        'exchangeRemoteConfigAtom',
        'assetsModule',
        'enabledAssetsAtom',
        'assetsAtom',
        'exchangeableAssetsAtom',
    ],
};
export default exchangeProvidersModuleDefinition;
